import { useEffect, useRef, useState } from 'react';
import type { ProvisionCanonicalComposition } from '@reform-lite/contracts';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useT } from '@/i18n/use-t';
import {
  useApplyProvisionCompositionDraft,
  usePreviewProvisionCompositionDraft,
  useProvisionCompositionHistory,
  useSaveProvisionCompositionDraft,
  type ProvisionApplication,
  type ProvisionCompositionDraft,
  type ProvisionCompositionDraftPreview,
  type ProvisionCompositionProfileSelection,
} from '@/hooks/use-provision';

export function AdvancedCompositionEditor({
  application,
  profile,
  draft,
  desiredValues,
}: {
  application: ProvisionApplication;
  profile: ProvisionCompositionProfileSelection;
  draft: ProvisionCompositionDraft | null;
  desiredValues: Record<string, unknown>;
}) {
  const t = useT('admin_ui');
  const initial = manualCompositionSource({ application, profile, draft, desiredValues });
  const sourceKey = `${application.id}:${draft?.version ?? 'desired'}`;
  const initializedKey = useRef('');
  const [text, setText] = useState(() => prettyJson(initial));
  const [sourceVersion, setSourceVersion] = useState(draft?.version ?? null);
  const [selections, setSelections] = useState(() => manualSelections(draft));
  const [acknowledgedWarningCodes, setAcknowledgedWarningCodes] = useState(
    draft?.acknowledgedWarningCodes ?? [],
  );
  const [parseError, setParseError] = useState<string | null>(null);
  const [preview, setPreview] = useState<ProvisionCompositionDraftPreview | null>(null);
  const [reason, setReason] = useState('');
  const [confirmLargeScope, setConfirmLargeScope] = useState(false);
  const [applyResult, setApplyResult] = useState<{
    changed: boolean;
    compositionRevision: number;
    desiredStateRevision: number;
  } | null>(null);
  const saveDraft = useSaveProvisionCompositionDraft();
  const previewDraft = usePreviewProvisionCompositionDraft();
  const applyDraft = useApplyProvisionCompositionDraft();
  const busy = saveDraft.isPending || previewDraft.isPending || applyDraft.isPending;

  useEffect(() => {
    if (initializedKey.current === sourceKey) return;
    initializedKey.current = sourceKey;
    setText(prettyJson(initial));
    setSourceVersion(draft?.version ?? null);
    setSelections(manualSelections(draft));
    setAcknowledgedWarningCodes(draft?.acknowledgedWarningCodes ?? []);
    setParseError(null);
    setPreview(null);
    setApplyResult(null);
  }, [draft, initial, sourceKey]);

  function parseComposition() {
    try {
      const parsed = parseCanonicalComposition(JSON.parse(text));
      if (!parsed.ok) {
        setParseError(parsed.error);
        return null;
      }
      if (
        parsed.value.applicationInstanceId !== application.id ||
        parsed.value.applicationType !== application.intrinsicType
      ) {
        setParseError(
          t(
            'provision.composition.identityMismatch',
            'Application identity and type must match the selected Provision instance.',
          ),
        );
        return null;
      }
      setParseError(null);
      return parsed.value;
    } catch (error) {
      setParseError(error instanceof Error ? error.message : String(error));
      return null;
    }
  }

  async function saveAndPreview() {
    const composition = parseComposition();
    if (composition === null) return;
    setApplyResult(null);
    const saved = await saveDraft.mutateAsync({
      applicationId: application.id,
      draft: {
        schemaVersion: 1,
        expectedVersion: sourceVersion,
        authoringMode: 'manual',
        composition,
        selections,
        acknowledgedWarningCodes,
      },
    });
    initializedKey.current = `${application.id}:${saved.version}`;
    setSourceVersion(saved.version);
    setSelections(manualSelections(saved));
    const result = await previewDraft.mutateAsync({
      applicationId: application.id,
      expectedDraftVersion: saved.version,
    });
    setPreview(result);
  }

  async function applyPreview() {
    if (preview === null) return;
    const result = await applyDraft.mutateAsync({
      applicationId: application.id,
      request: {
        schemaVersion: 1,
        expectedDraftVersion: preview.draftVersion,
        expectedCompositionRevision: preview.expectedCompositionRevision,
        expectedProfileRevision: preview.expectedProfileRevision,
        expectedHistorySequence: preview.expectedHistorySequence,
        expectedObservationGeneration: preview.expectedObservationGeneration,
        expectedRecords: preview.expectedRecords,
        previewChecksum: preview.previewChecksum,
        reason,
        confirmLargeScope,
      },
    });
    setApplyResult(result);
  }

  const warningFindings = preview?.validation.findings.filter((finding) => finding.acknowledgeable);
  const warningsAcknowledged =
    warningFindings?.every((finding) => acknowledgedWarningCodes.includes(finding.reason.code)) ??
    true;

  return (
    <section className="space-y-4 rounded-lg border border-amber-500/40 bg-amber-500/5 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="font-semibold">
              {t('provision.composition.manualTitle', 'Advanced/manual canonical composition')}
            </h2>
            <Badge variant="outline">
              {t('provision.composition.provisionAdmin', 'Provision Admin')}
            </Badge>
            <Badge variant="outline">
              {t('provision.composition.customLegacySupported', 'Custom/legacy supported')}
            </Badge>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {t(
              'provision.composition.manualHelp',
              'Edit the complete canonical JSON. Unknown fields, scalar types, array order, and absent-versus-null values are preserved. Runtime secrets must remain external references.',
            )}
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            setText(prettyJson(initial));
            setParseError(null);
            setPreview(null);
          }}
        >
          {t('provision.composition.resetCanonical', 'Reset to saved canonical value')}
        </Button>
      </div>
      <Textarea
        aria-label={t(
          'provision.composition.manualJsonLabel',
          'Advanced manual canonical composition JSON',
        )}
        className="min-h-[420px] font-mono text-xs"
        spellCheck={false}
        value={text}
        onChange={(event) => {
          setText(event.target.value);
          setPreview(null);
          setApplyResult(null);
        }}
      />
      {parseError !== null && (
        <pre className="whitespace-pre-wrap rounded-md border border-destructive/40 p-3 text-xs text-destructive">
          {parseError}
        </pre>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <Button disabled={busy} onClick={() => void saveAndPreview()}>
          {busy
            ? t('provision.composition.working', 'Working…')
            : t('provision.composition.saveManualDraftPreview', 'Save manual draft & preview')}
        </Button>
        <span className="text-xs text-muted-foreground">
          {t(
            'provision.composition.manualNoOpHelp',
            'Saving a byte-equivalent canonical value and confirming it is a server-verified no-op.',
          )}
        </span>
      </div>
      <MutationError error={saveDraft.error ?? previewDraft.error ?? applyDraft.error} />

      {preview !== null && (
        <div className="space-y-4 border-t pt-4">
          <div className="flex flex-wrap gap-2">
            <Badge variant={preview.validation.valid ? 'success' : 'destructive'}>
              {preview.validation.valid
                ? t('provision.composition.badgeValid', 'valid')
                : t('provision.composition.badgeBlocked', 'blocked')}
            </Badge>
            <Badge variant="outline">
              {t(
                'provision.composition.desiredStateChangesCount',
                '{count} desired-state changes',
              ).replace('{count}', String(preview.desiredStateChanges.length))}
            </Badge>
            <Badge variant="outline">
              {t('provision.composition.portActionsCount', '{count} port actions').replace(
                '{count}',
                String(preview.portClaims.length),
              )}
            </Badge>
            <Badge variant="outline">
              {preview.validation.strictestActivationPolicy.replaceAll('_', ' ')}
            </Badge>
          </div>
          {preview.desiredStateChanges.length === 0 && (
            <p className="rounded-md border border-emerald-500/40 bg-emerald-500/5 p-3 text-sm">
              {t(
                'provision.composition.noRawFieldChanges',
                'No materialized raw field changes. Apply will retain the current canonical revision.',
              )}
            </p>
          )}
          {preview.desiredStateChanges.length > 0 && (
            <DifferenceList
              title={t(
                'provision.composition.materializedChanges',
                'Materialized raw-field changes',
              )}
              differences={preview.desiredStateChanges}
            />
          )}
          {preview.validation.findings.map((finding) => (
            <div
              key={`${finding.reason.code}-${finding.entityId}`}
              className="rounded-md border p-3 text-sm"
            >
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant={finding.severity === 'error' ? 'destructive' : 'outline'}>
                  {finding.severity}
                </Badge>
                <code>{finding.reason.code}</code>
                <span>{finding.reason.message}</span>
              </div>
              {finding.acknowledgeable && (
                <label className="mt-2 flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={acknowledgedWarningCodes.includes(finding.reason.code)}
                    onChange={(event) => {
                      const codes = new Set(acknowledgedWarningCodes);
                      if (event.target.checked) codes.add(finding.reason.code);
                      else codes.delete(finding.reason.code);
                      setAcknowledgedWarningCodes(Array.from(codes).sort());
                      setPreview(null);
                    }}
                  />
                  {t(
                    'provision.composition.acknowledgeEvidenceWarning',
                    'Acknowledge this permitted evidence warning, then save and preview again',
                  )}
                </label>
              )}
            </div>
          ))}
          {preview.validation.dependencyOrder.length > 0 && (
            <div>
              <h3 className="text-sm font-medium">
                {t('provision.composition.dependencyOrder', 'Dependency installation order')}
              </h3>
              <ol className="mt-1 list-decimal pl-5 text-sm text-muted-foreground">
                {preview.validation.dependencyOrder.map((id) => (
                  <li key={id}>{id}</li>
                ))}
              </ol>
            </div>
          )}
          <Input
            aria-label={t(
              'provision.composition.manualApplyReasonLabel',
              'Manual composition apply reason',
            )}
            required
            placeholder={t(
              'provision.composition.manualApplyReasonPlaceholder',
              'Reason for this audited manual composition',
            )}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
          {preview.largeScopeConfirmationRequired && (
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={confirmLargeScope}
                onChange={(event) => setConfirmLargeScope(event.target.checked)}
              />
              {t(
                'provision.composition.confirmLargeScope',
                'Confirm impact on {count} same-host applications',
              ).replace('{count}', String(preview.affectedApplicationIds.length))}
            </label>
          )}
          <Button
            disabled={
              busy ||
              !preview.validation.valid ||
              !warningsAcknowledged ||
              reason.trim().length === 0 ||
              (preview.largeScopeConfirmationRequired && !confirmLargeScope)
            }
            onClick={() => void applyPreview()}
          >
            {t('provision.composition.confirmManual', 'Confirm audited manual composition')}
          </Button>
        </div>
      )}
      {applyResult !== null && (
        <div className="rounded-md border border-emerald-500/40 bg-emerald-500/5 p-3 text-sm">
          {applyResult.changed
            ? t('provision.composition.manualApplied', 'Manual composition applied.')
            : t(
                'provision.composition.canonicalUnchanged',
                'Canonical composition unchanged.',
              )}{' '}
          {t(
            'provision.composition.revisions',
            'Composition revision {composition}; desired-state revision {desired}.',
          )
            .replace('{composition}', String(applyResult.compositionRevision))
            .replace('{desired}', String(applyResult.desiredStateRevision))}
        </div>
      )}
    </section>
  );
}

export function CompositionHistory({ applicationId }: { applicationId: string }) {
  const t = useT('admin_ui');
  const history = useProvisionCompositionHistory(applicationId);
  return (
    <section className="rounded-lg border p-4">
      <h2 className="font-semibold">
        {t('provision.composition.historyTitle', 'Immutable composition history')}
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">
        {t(
          'provision.composition.historyHelp',
          'Applied canonical snapshots remain intelligible if catalogue entries are later archived. Secret-looking material is redacted defensively; secret references remain references.',
        )}
      </p>
      <div className="mt-3 space-y-2">
        {history.isLoading && (
          <p className="text-sm text-muted-foreground">
            {t('provision.composition.loadingHistory', 'Loading history…')}
          </p>
        )}
        {history.data?.map((entry) => (
          <details key={entry.historyId} className="rounded-md border p-3">
            <summary className="cursor-pointer text-sm">
              <strong>
                {t('provision.composition.historyRevision', 'Revision {sequence}').replace(
                  '{sequence}',
                  String(entry.sequence),
                )}
              </strong>{' '}
              · {entry.reason} · {new Date(entry.createdAt).toLocaleString()}
            </summary>
            <div className="mt-2 flex flex-wrap gap-2 text-xs">
              <Badge variant="outline">
                {t('provision.composition.historyProfile', 'profile {value}').replace(
                  '{value}',
                  entry.profileVersionId ??
                    t('provision.composition.manualUnpinned', 'manual/unpinned'),
                )}
              </Badge>
              <Badge variant="outline">
                {t('provision.composition.historyActor', 'actor {value}').replace(
                  '{value}',
                  entry.createdBy ?? t('provision.composition.actorSystem', 'system'),
                )}
              </Badge>
              <code className="break-all">{entry.checksum}</code>
            </div>
            <pre className="mt-3 max-h-96 overflow-auto whitespace-pre-wrap rounded-md bg-muted p-3 text-xs">
              {prettyJson(redactForDisplay(entry.composition))}
            </pre>
          </details>
        ))}
        {history.isSuccess && history.data.length === 0 && (
          <p className="text-sm text-muted-foreground">
            {t('provision.composition.noHistory', 'No applied composition history yet.')}
          </p>
        )}
        <MutationError error={history.error} />
      </div>
    </section>
  );
}

export function manualCompositionSource(params: {
  application: ProvisionApplication;
  profile: ProvisionCompositionProfileSelection;
  draft: ProvisionCompositionDraft | null;
  desiredValues: Record<string, unknown>;
}): ProvisionCanonicalComposition {
  if (params.draft !== null) return params.draft.composition;
  const hardware = recordValue(params.desiredValues['hardware']);
  const desiredSoftware = recordValue(params.desiredValues['software']);
  const software = {
    ...desiredSoftware,
    components: recordValue(desiredSoftware['components']),
  };
  const extensions = recordValue(params.desiredValues['extensions']);
  return {
    schemaVersion: 1,
    applicationInstanceId: params.application.id,
    applicationType: params.application.intrinsicType,
    profileVersionId: params.profile.selected?.versionId ?? null,
    hardware: hardware as ProvisionCanonicalComposition['hardware'],
    software: software as ProvisionCanonicalComposition['software'],
    ...(Object.keys(extensions).length === 0
      ? {}
      : { extensions: extensions as ProvisionCanonicalComposition['extensions'] }),
  };
}

export function redactForDisplay(value: unknown, key = ''): unknown {
  if (/^secretReference$/i.test(key)) return value;
  if (
    /(?:secret(?!reference)|password|token|private.?key|certificate|api.?key|card.?number|cvv|cvc)/i.test(
      key,
    )
  ) {
    return '[REDACTED]';
  }
  if (Array.isArray(value)) return value.map((item) => redactForDisplay(item));
  if (typeof value !== 'object' || value === null) return value;
  return Object.fromEntries(
    Object.entries(value).map(([nestedKey, nested]) => [
      nestedKey,
      redactForDisplay(nested, nestedKey),
    ]),
  );
}

export function parseCanonicalComposition(
  value: unknown,
): { ok: true; value: ProvisionCanonicalComposition } | { ok: false; error: string } {
  if (!recordOrNull(value)) return { ok: false, error: 'Canonical composition must be an object.' };
  if (value['schemaVersion'] !== 1) return { ok: false, error: 'schemaVersion must be 1.' };
  if (typeof value['applicationInstanceId'] !== 'string') {
    return { ok: false, error: 'applicationInstanceId must be a string.' };
  }
  if (!['pos', 'sco', 'attendant'].includes(String(value['applicationType']))) {
    return { ok: false, error: 'applicationType must be pos, sco, or attendant.' };
  }
  if (!(value['profileVersionId'] === null || typeof value['profileVersionId'] === 'string')) {
    return { ok: false, error: 'profileVersionId must be a string or null.' };
  }
  if (!recordOrNull(value['hardware'])) return { ok: false, error: 'hardware must be an object.' };
  if (!recordOrNull(value['software']) || !recordOrNull(value['software']['components'])) {
    return { ok: false, error: 'software.components must be an object.' };
  }
  return { ok: true, value: value as ProvisionCanonicalComposition };
}

function manualSelections(draft: ProvisionCompositionDraft | null) {
  return (draft?.selections ?? []).map((selection) => ({
    ...selection,
    manualMode: true as const,
  }));
}

function DifferenceList({
  title,
  differences,
}: {
  title: string;
  differences: Array<{
    path: string;
    kind: string;
    before: { present: boolean; value?: unknown };
    after: { present: boolean; value?: unknown };
  }>;
}) {
  const t = useT('admin_ui');
  return (
    <div>
      <h3 className="text-sm font-medium">{title}</h3>
      <div className="mt-2 space-y-2">
        {differences.map((difference) => (
          <div
            key={`${difference.path}-${difference.kind}`}
            className="rounded-md border p-2 text-xs"
          >
            <code>{difference.path}</code> · {difference.kind}
            <div className="mt-1 grid gap-2 md:grid-cols-2">
              <pre className="overflow-auto bg-muted p-2">
                {t('provision.composition.diffBefore', 'before:')}{' '}
                {displayDifferenceValue(difference.before)}
              </pre>
              <pre className="overflow-auto bg-muted p-2">
                {t('provision.composition.diffAfter', 'after:')}{' '}
                {displayDifferenceValue(difference.after)}
              </pre>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function displayDifferenceValue(value: { present: boolean; value?: unknown }) {
  return value.present ? prettyJson(redactForDisplay(value.value)) : '<absent>';
}

function prettyJson(value: unknown) {
  return JSON.stringify(value, null, 2);
}

function recordValue(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function recordOrNull(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function MutationError({ error }: { error: unknown }) {
  if (error === null || error === undefined) return null;
  const message =
    typeof error === 'object' && error !== null && 'message' in error
      ? String((error as { message: unknown }).message)
      : String(error);
  return (
    <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
      {message}
    </div>
  );
}
